import type { ReactNode } from "react";
import { useTimelineCtx } from "../ctx";
import { TimelineMarkerEnum, type ITimelineSetSettings } from "../types";
import { parseTimelineCustomText } from "../utils";
import { TimelineMarker } from "./timeline-marker";

interface IProps {
  itemIdx: number;
  editableMarkerSlot?: ReactNode;
  /** Bare editable text node (no marker wrapper) — used for marker
   *  shapes whose own wrapper IS the marker, like h-wide's wide chevron
   *  with a centred label. */
  editableTextSlot?: ReactNode;
  setSettings: ITimelineSetSettings;
}

export const TimelineMarkerFactory = ({
  itemIdx,
  setSettings,
  editableMarkerSlot,
  editableTextSlot,
}: IProps) => {
  const {
    isVertical,
    comboSettings: {
      timelineMarker,
      timelineArrowVariant,
      timelineMarkerShape,
      timelineSnakeOrder,
      timelineSetsPerSlide,
    },
  } = useTimelineCtx();

  // Snake flips direction on odd rows (RTL traversal) — arrows need to
  // point along the snake's current direction.
  const snakePerRow =
    !timelineSetsPerSlide || timelineSetsPerSlide === "auto"
      ? 3
      : Number(timelineSetsPerSlide);
  const isSnakeRTLRow =
    timelineSnakeOrder && Math.floor(itemIdx / snakePerRow) % 2 === 1;

  // Arrow SVGs point right; rotate for vertical flow or RTL snake row.
  const arrowSvgStyle: React.CSSProperties = {
    display: "block",
    transform: isVertical
      ? "rotate(90deg)"
      : isSnakeRTLRow
        ? "rotate(180deg)"
        : undefined,
  };

  // Wide chevron — the shape IS the track for this item (no separate
  // line drawn). Arrows fall through to normal rendering to avoid
  // nesting one shape inside another.
  if (
    timelineMarkerShape === "arrow-big" &&
    timelineMarker !== TimelineMarkerEnum.Arrows
  ) {
    let label: ReactNode = null;
    const isCustomText = timelineMarker === TimelineMarkerEnum.CustomText;
    if (timelineMarker === TimelineMarkerEnum.Numbers) {
      label = (itemIdx + 1).toString();
    } else if (isCustomText) {
      // editableTextSlot makes the chevron's label directly contenteditable
      // in the editor; absent (read-only / published view), parse the
      // static string.
      label = editableTextSlot
        ? editableTextSlot
        : parseTimelineCustomText(setSettings.timelineMarkerText, itemIdx);
    } else if (timelineMarker === TimelineMarkerEnum.Bullets) {
      label = (
        <span className="tw-block tw-size-2 tw-rounded-full tw-bg-white" />
      );
    }
    return (
      <TimelineMarker
        itemIdx={itemIdx}
        className="timeline-marker-shape-arrow-big tw-relative"
      >
        <svg
          viewBox="0 0 336 56"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          preserveAspectRatio="none"
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            display: "block",
          }}
        >
          <path
            d="M0 0H320L336 28L320 56H0L16 28L0 0Z"
            fill="var(--timeline-color)"
          />
        </svg>
        <span
          style={{
            position: "relative",
            zIndex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "var(--timeline-marker-text-color, #FFFFFF)",
            fontFamily: "Inter, sans-serif",
            fontWeight: 600,
            fontSize: isCustomText ? 22 : 18,
            lineHeight: "22px",
            width: "100%",
            // Don't set `height: 100%` — that disables flex stretch in
            // some browsers and the text aligns to the top.
            flex: "1 1 auto",
            padding: isCustomText ? "0 16px" : 0,
          }}
        >
          {label}
        </span>
      </TimelineMarker>
    );
  }

  if (timelineMarker === TimelineMarkerEnum.Bullets) {
    return (
      <TimelineMarker
        itemIdx={itemIdx}
        className="tw-size-5 tw-min-h-5 tw-min-w-5 tw-rounded-full"
      />
    );
  }

  if (timelineMarker === TimelineMarkerEnum.Arrows) {
    if (timelineSnakeOrder) {
      return (
        <TimelineMarker
          itemIdx={itemIdx}
          className="timeline-marker-arrow timeline-marker-arrow--small"
        >
          <svg
            width="32"
            height="32"
            viewBox="0 0 32 32"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={arrowSvgStyle}
          >
            <path
              d="M6.00112 4L5.77712 4.012C5.42146 4.05188 5.08303 4.18647 4.79711 4.40172C4.51119 4.61696 4.28827 4.90499 4.15159 5.23575C4.01492 5.5665 3.9695 5.92788 4.02007 6.28217C4.07064 6.63646 4.21536 6.97069 4.43912 7.25L11.4411 16L4.44112 24.75C4.20568 25.0441 4.05808 25.3986 4.0153 25.7729C3.97253 26.1471 4.03631 26.5259 4.19932 26.8655C4.36233 27.2051 4.61793 27.4917 4.93669 27.6924C5.25546 27.8932 5.62442 27.9998 6.00112 28H18.0011C18.3007 27.9998 18.5964 27.9323 18.8665 27.8025C19.1365 27.6727 19.3739 27.4839 19.5611 27.25L27.5611 17.25C27.8451 16.8953 27.9999 16.4544 27.9999 16C27.9999 15.5456 27.8451 15.1047 27.5611 14.75L19.5611 4.75C19.3739 4.51613 19.1365 4.32731 18.8665 4.19749C18.5964 4.06768 18.3007 4.00019 18.0011 4H6.00112Z"
              fill="var(--timeline-color)"
            />
          </svg>
        </TimelineMarker>
      );
    }

    // Vertical always uses the small variant — the 48x56 big chevron
    // is a horizontal-only design.
    if (timelineArrowVariant === "small" || isVertical) {
      return (
        <TimelineMarker
          itemIdx={itemIdx}
          className="timeline-marker-arrow timeline-marker-arrow--small"
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={arrowSvgStyle}
          >
            <path
              d="M2.00112 0L1.77712 0.0120001C1.42146 0.0518831 1.08303 0.186467 0.797108 0.401715C0.51119 0.616963 0.288266 0.904989 0.151591 1.23575C0.0149162 1.5665 -0.0305027 1.92788 0.020071 2.28217C0.0706448 2.63646 0.215358 2.97069 0.439117 3.25L7.44112 12L0.441117 20.75C0.205684 21.0441 0.058082 21.3986 0.0153046 21.7729C-0.0274729 22.1471 0.0363139 22.5259 0.199321 22.8655C0.362328 23.2051 0.617927 23.4917 0.936691 23.6924C1.25546 23.8932 1.62442 23.9998 2.00112 24H14.0011C14.3007 23.9998 14.5964 23.9323 14.8665 23.8025C15.1365 23.6727 15.3739 23.4839 15.5611 23.25L23.5611 13.25C23.8451 12.8953 23.9999 12.4544 23.9999 12C23.9999 11.5456 23.8451 11.1047 23.5611 10.75L15.5611 0.75C15.3739 0.516127 15.1365 0.327309 14.8665 0.197495C14.5964 0.0676804 14.3007 0.000187221 14.0011 0H2.00112Z"
              fill="var(--timeline-color)"
            />
          </svg>
        </TimelineMarker>
      );
    }

    return (
      <TimelineMarker
        itemIdx={itemIdx}
        className="timeline-marker-arrow timeline-marker-arrow--big"
      >
        <svg
          viewBox="0 0 48 56"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          preserveAspectRatio="none"
          style={{
            width: "100%",
            height: "100%",
            display: "block",
            transform: isVertical
              ? "rotate(90deg)"
              : isSnakeRTLRow
                ? "rotate(180deg)"
                : undefined,
          }}
        >
          <path
            d="M0 0H32L48 28L32 56H0L16 28L0 0Z"
            fill="var(--timeline-color)"
          />
        </svg>
      </TimelineMarker>
    );
  }

  if (timelineMarker === "numbers") {
    return (
      <TimelineMarker itemIdx={itemIdx}>
        {(itemIdx + 1).toString().padStart(2, "0")}
      </TimelineMarker>
    );
  }

  if (editableMarkerSlot) {
    return <>{editableMarkerSlot}</>;
  }

  return (
    <TimelineMarker itemIdx={itemIdx}>
      {parseTimelineCustomText(setSettings.timelineMarkerText, itemIdx)}
    </TimelineMarker>
  );
};
